import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Store, PenLine } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice } from "../../lib/api";
import { stockOf } from "../../lib/stock";

const errMsg = (e, fallback) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : fallback);
const INPUT = "w-full bg-[var(--bg-cream)] border border-[var(--border)] rounded-lg px-3 py-2 text-sm text-[var(--ink-forest)] focus:outline-none focus:border-[var(--terracotta)]";

function SwapForm({ order, onUpdated, onClose }) {
  const [kind, setKind] = useState("product");
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [productId, setProductId] = useState("");
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [qty, setQty] = useState(1);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data || [])).catch(() => {});
  }, []);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => !q || p.name.toLowerCase().includes(q)).slice(0, 8);
  }, [products, query]);
  const picked = products.find((p) => p.id === productId);

  const add = async () => {
    if (kind === "product" && !picked) return toast.error("Pick a product from the shop");
    if (kind === "custom" && !name.trim()) return toast.error("Add a name for this item");
    setSaving(true);
    try {
      const body = kind === "product"
        ? { kind, product_id: productId, quantity: qty }
        : { kind, name, price: parseFloat(price) || 0, quantity: qty };
      const r = await api.post(`/admin/orders/${order.id}/swaps`, body);
      onUpdated(r.data);
      toast.success("Swap added");
      onClose();
    } catch (e) {
      toast.error(errMsg(e, "Could not add the swap"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[var(--bg-cream)] border border-[var(--terracotta)]/30 rounded-lg p-3 space-y-3" data-testid="swap-form">
      <div className="flex gap-2 flex-wrap">
        {[["product", "From the shop", Store], ["custom", "Not online", PenLine]].map(([id, label, Icon]) => (
          <button
            key={id}
            type="button"
            onClick={() => setKind(id)}
            className={`rounded-full px-3 py-1.5 text-xs font-medium border inline-flex items-center gap-1.5 ${kind === id ? "bg-[var(--ink-forest)] text-[var(--bg-cream)] border-[var(--ink-forest)]" : "bg-[var(--bg-surface)] text-[var(--ink-forest)] border-[var(--border)]"}`}
            data-testid={`swap-kind-${id}`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {kind === "product" ? (
        <div className="space-y-2">
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search the shop…" className={INPUT} data-testid="swap-product-search" />
          <div className="max-h-44 overflow-y-auto border border-[var(--border)] rounded-lg divide-y divide-[var(--border)] bg-[var(--bg-surface)]">
            {matches.map((p) => {
              const stock = stockOf(p);
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setProductId(p.id)}
                  disabled={stock <= 0}
                  className={`w-full text-left px-3 py-2 text-xs flex justify-between gap-2 disabled:opacity-40 ${productId === p.id ? "bg-[var(--terracotta)]/10" : "hover:bg-[var(--muted)]/60"}`}
                  data-testid={`swap-product-${p.id}`}
                >
                  <span className="truncate text-[var(--ink-forest)]">{p.name}</span>
                  <span className="shrink-0 text-[var(--muted-fg)]">{formatPrice(p.price)} · {stock} in stock</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Rope & ball dog toy" className={`${INPUT} col-span-2`} data-testid="swap-custom-name" />
          <input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Price $" className={INPUT} data-testid="swap-custom-price" />
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-xs text-[var(--muted-fg)] inline-flex items-center gap-2">
          Quantity
          <input type="number" min="1" max={kind === "product" && picked ? stockOf(picked) : 100} value={qty} onChange={(e) => setQty(Math.max(1, parseInt(e.target.value, 10) || 1))} className="w-16 bg-[var(--bg-surface)] border border-[var(--border)] rounded-lg px-2 py-1 text-sm" data-testid="swap-qty" />
        </label>
        <button onClick={add} disabled={saving} className="bg-[var(--terracotta)] text-white rounded-full px-4 py-1.5 text-xs font-medium disabled:opacity-60" data-testid="swap-add-btn">
          Add to order
        </button>
        <button onClick={onClose} className="text-xs text-[var(--muted-fg)] underline" data-testid="swap-cancel-btn">Cancel</button>
      </div>
      {kind === "product" && <p className="text-[11px] text-[var(--muted-fg)]">Shop items come out of stock as soon as you add them.</p>}
    </div>
  );
}

export default function SwapsSection({ order, onUpdated }) {
  const [adding, setAdding] = useState(false);
  const swaps = order.swaps || [];

  const remove = async (s) => {
    if (!window.confirm(`Take ${s.name} off this order?${s.product_id ? " It goes back into stock." : ""}`)) return;
    try {
      const r = await api.delete(`/admin/orders/${order.id}/swaps/${s.id}`);
      onUpdated(r.data);
      toast.success("Swap removed");
    } catch (e) {
      toast.error(errMsg(e, "Could not remove"));
    }
  };

  return (
    <div className="space-y-2" data-testid="swaps-section">
      {swaps.length > 0 && (
        <>
          <p className="eyebrow">Swaps</p>
          {swaps.map((s) => (
            <div key={s.id} className="flex items-center gap-3 text-sm" data-testid={`swap-row-${s.id}`}>
              <span className="flex-1 min-w-0 truncate text-[var(--ink-forest)]">{s.quantity} × {s.name}</span>
              <span className="shrink-0 text-[10px] uppercase tracking-wider text-[var(--muted-fg)]">{s.product_id ? "from the shop" : "not online"}</span>
              <span className="text-[var(--ink-forest)]">{formatPrice(s.price * s.quantity)}</span>
              <button onClick={() => remove(s)} className="text-[var(--muted-fg)] hover:text-[var(--terracotta)]" aria-label="Remove swap" data-testid={`swap-remove-${s.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </>
      )}
      {adding ? (
        <SwapForm order={order} onUpdated={onUpdated} onClose={() => setAdding(false)} />
      ) : (
        <button onClick={() => setAdding(true)} className="text-xs text-[var(--terracotta)] inline-flex items-center gap-1 hover:underline" data-testid="add-swap-btn">
          <Plus className="w-3.5 h-3.5" /> Add a swap
        </button>
      )}
    </div>
  );
}
